import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';

import {Metric, MetricsRequest} from 'app_api/projects/metrics/types';
import {BehaviorEntity} from 'app_api/projects/behaviors/types';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {MultiMetricsSectionItem} from 'common/query-builder/query-block/multi-metrics-menu/types';
import {isCohortsShowClause, isFormulaShowClause} from 'common/report/insights/models/utils';
import {EntityType, SharableEntityType} from 'common/report/util/saved-entities/types';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {BehaviorsRequest} from 'app_api/projects/behaviors/types';
import {useBehaviors} from 'common/contexts/react/behaviors-context';
import {useTranslation} from 'common/i18n/hooks';

import {ShowClause} from '../util/types';
import {
  getMetricTrackingProps,
  MetricsTrackingEvent,
  SaveMetricEventType,
  trackMetricsEvent,
} from '../util/metric-tracking';
import {getUpdatedFormulaAndMetrics} from './formula-utils';
import {
  getClauseFromBehaviorModel,
  getClauseFromMetricModel,
  getMetricAtIndex,
  getShowClauseFromMetricItemSelection,
  updateMetricOrReferencedMetric,
} from './helpers';
import {useReportBookmark} from './report-bookmark-manager';
import {attemptRequest, useSaveAndShareFlow} from './save-utils';
import {UI_STATE_BEHAVIOR_FIELDS} from './constants';
import {getSerializedBehaviorDefinition, getSerializedClauseDefinition} from './utils/bookmark';

export function useMetricEntries() {
  const {t} = useTranslation();
  const metrics = useMetrics();
  const behaviors = useBehaviors();
  const {params: bookmarkParams, updateParams} = useReportBookmark();

  const saveAndShareMetric = useSaveAndShareFlow<Metric, MetricsRequest>({
    createFn: (entity) => metrics.create(entity),
    entityType: EntityType.Metric,
    existingNamesFn: () => metrics.getAllValues().map((metric) => metric.name),
    sharableEntityType: SharableEntityType.Metric,
  });

  const saveAndShareBehavior = useSaveAndShareFlow<BehaviorEntity, BehaviorsRequest>({
    entityType: EntityType.Behavior,
    existingNamesFn: () => behaviors.getAllValues().map((behavior) => behavior.name),
    createFn: (entity) => behaviors.create(entity),
  });

  /**
   * Updates an existing metric, clones or creates a new entity
   * This handles both behavior & formula based metrics
   */
  const saveOrCreateMetricFromClause = async ({
    clause,
    saveAsNew = false,
  }: {
    clause: ShowClause;
    saveAsNew?: boolean;
  }) => {
    let metricEntity: MetricsRequest;

    if (isFormulaShowClause(clause)) {
      // preps referenced metrics for saving
      const {definition: formulaDefinition, referencedMetrics} = getUpdatedFormulaAndMetrics({
        formula: clause,
        // when saving a formula, the metrics referenced in it can come from the main report or metrics inside the formula
        clauses: clause.referencedMetrics?.length ? clause.referencedMetrics : bookmarkParams.sections.show,
        validate: true,
      });

      metricEntity = {
        type: BookmarkTypes.TopLevelMetricType.Formula,
        name: clause.name!,
        description: ``,
        definition: {
          formula: {
            definition: formulaDefinition,

            // makes sure we store only canonical metric definitions in the saved formula
            referencedMetrics: referencedMetrics.map(
              (metric) => getSerializedClauseDefinition(metric) as BookmarkTypes.BehaviorShowClause,
            ),
          },
          measurement: clause.measurement,
          display: clause.display,
        },
      };
    } else {
      metricEntity = {
        type: BookmarkTypes.TopLevelMetricType.Metric,
        name: clause.name!,
        description: ``,
        definition: {
          // makes sure we store the canonical behavior definition in the metric
          behavior: getSerializedBehaviorDefinition(clause.behavior!),
          measurement: clause.measurement!,
          display: clause.display,
        },
      };
    }

    let metric: Metric | null;
    // Saving an existing metric.
    if (clause.id && !saveAsNew) {
      // when saving an existing metric, patch the model instead
      metric = await attemptRequest(() => metrics.patch(clause.id!, metricEntity));
      trackMetricsEvent(MetricsTrackingEvent.SaveMetric, {
        eventType: SaveMetricEventType.Update,
        ...getMetricTrackingProps(metric),
      });
      // Duplicating an existing metric.
    } else if (clause.id && saveAsNew) {
      // duplicating an existing metric will
      metric = await saveAndShareMetric(metricEntity, {isDuplicating: true});
      trackMetricsEvent(MetricsTrackingEvent.SaveMetric, {
        eventType: SaveMetricEventType.Duplicate,
        ...getMetricTrackingProps(metric),
        originalId: clause.id,
      });
      // Creating a new metric.
    } else {
      metric = await saveAndShareMetric(metricEntity);
      trackMetricsEvent(MetricsTrackingEvent.SaveMetric, {
        eventType: SaveMetricEventType.Create,
        ...getMetricTrackingProps(metric),
      });
    }

    if (!metric) {
      return null;
    }

    return getClauseFromMetricModel(metric);
  };

  /**
   * Updates an existing behavior in place, clones or creates a new entity
   */
  const saveOrCreateBehaviorFromClause = async ({
    clause,
    saveAsNew = false,
  }: {
    clause: ShowClause;
    saveAsNew?: boolean;
  }): Promise<ShowClause | null> => {
    if (isFormulaShowClause(clause)) {
      throw new Error(`attempted to save a behavior inside a formula.`);
    }

    const clauseBehavior = clause.behavior!;

    const entity: BehaviorEntity = {
      type: clauseBehavior.type!,
      name: clauseBehavior.name!,
      description: ``,
      definition: {
        behavior: omit(clauseBehavior, [...UI_STATE_BEHAVIOR_FIELDS, `id`, `hasUnsavedChanges`, `name`, `renamed`]),
      },
    };

    let behavior: BehaviorEntity | null;
    if (clauseBehavior.id && !saveAsNew) {
      // when saving an existing behavior, patch the model instead
      behavior = await attemptRequest(() => behaviors.patch(clauseBehavior.id!, entity));
    } else if (clause.id && saveAsNew) {
      // duplicating an existing behavior will show the save & share flow
      behavior = await saveAndShareBehavior(entity, {isDuplicating: true});
    } else {
      behavior = await saveAndShareBehavior(entity);
    }

    if (!behavior) {
      return null;
    }

    return getClauseFromBehaviorModel({behavior, clause});
  };

  /**
   * Handles saving a a top level metric or a metric nested inside a formula
   */
  const saveMetric = async ({
    index,
    parentMetricIndex,
    saveAsNew = false,
  }: {
    index: number;
    parentMetricIndex?: number;
    saveAsNew?: boolean;
  }) => {
    const clause = getMetricAtIndex({
      index,
      parentMetricIndex,
      params: bookmarkParams,
    });

    if (!clause) {
      return;
    }

    const newClause = await saveOrCreateMetricFromClause({clause, saveAsNew});

    if (newClause) {
      updateParams((params) => {
        updateMetricOrReferencedMetric({
          index,
          parentMetricIndex,
          params,
          newClause,
        });
      });
    }
  };

  /**
   * Handles a selection from the metrics menu for a given top level metric or a metric nested inside a formula
   */
  const selectMetricItem = ({
    index,
    parentMetricIndex,
    dataGroupId,
    selectedItem,
    retainUserFilters,
  }: {
    index: number;
    parentMetricIndex?: number;
    dataGroupId: string | null;
    selectedItem: MultiMetricsSectionItem;

    /** when set, retains any user based filters */
    retainUserFilters?: boolean;
  }) => {
    const clause = getMetricAtIndex({
      index,
      parentMetricIndex,
      params: bookmarkParams,
      type: BookmarkTypes.TopLevelMetricType.Metric,
    });

    const newClause = getShowClauseFromMetricItemSelection({
      dataGroupId,
      selectedItem,
    });

    // retains user filters when selecting user based entities
    if (retainUserFilters && clause && isCohortsShowClause(clause) && isCohortsShowClause(newClause)) {
      newClause.behavior!.filters = cloneDeep(clause.behavior!.filters);
    }

    updateParams((params) => {
      updateMetricOrReferencedMetric({
        index,
        parentMetricIndex,
        params,
        newClause,
      });
    });
  };

  /**
   * Resets a top level metric or a metric nested inside a formula to the saved entity state
   */
  const resetMetric = ({index, parentMetricIndex}: {index: number; parentMetricIndex?: number}) => {
    const clause = getMetricAtIndex({index, parentMetricIndex, params: bookmarkParams});

    if (!clause) {
      return;
    }

    const model = clause.id ? metrics.getOne(clause.id) : null;

    if (!model?.value) {
      return;
    }

    updateParams((params) =>
      updateMetricOrReferencedMetric({
        index,
        parentMetricIndex,
        params,
        newClause: getClauseFromMetricModel(model.value!),
      }),
    );
  };

  /**
   * Handles saving a behavior inside a metric or referenced metric in a formula
   */
  const saveBehavior = async ({
    index,
    parentMetricIndex,
    saveAsNew = false,
  }: {
    index: number;
    parentMetricIndex?: number;
    saveAsNew?: boolean;
  }) => {
    const clause = getMetricAtIndex({
      index,
      parentMetricIndex,
      params: bookmarkParams,
    });

    if (!clause) {
      return;
    }

    const newClause = await saveOrCreateBehaviorFromClause({clause, saveAsNew});

    if (newClause) {
      updateParams((params) => {
        updateMetricOrReferencedMetric({
          index,
          parentMetricIndex,
          params,
          newClause,
        });
      });
    }
  };

  /**
   * Resets a saved behavior to its saved entity state
   */
  const resetBehavior = ({index, parentMetricIndex}: {index: number; parentMetricIndex?: number}) => {
    const clause = getMetricAtIndex({
      index,
      parentMetricIndex,
      type: BookmarkTypes.TopLevelMetricType.Metric,
      params: bookmarkParams,
    });

    if (!clause) {
      return;
    }

    const model = clause.behavior?.id ? behaviors.getOne(clause.behavior.id) : null;

    if (!model?.value) {
      return;
    }

    updateParams((params) =>
      updateMetricOrReferencedMetric({
        index,
        parentMetricIndex,
        params,
        newClause: getClauseFromBehaviorModel({behavior: model.value!, clause}),
      }),
    );
  };

  const getSaveBehaviorDisabledMessage = ({
    index,
    parentMetricIndex,
    omitPermissionsCheck = false,
  }: {
    index: number;
    parentMetricIndex?: number;
    omitPermissionsCheck?: boolean;
  }): string | null => {
    const clause = getMetricAtIndex({
      index,
      parentMetricIndex,
      params: bookmarkParams,
    });

    if (!clause) {
      return null;
    }

    if (isFormulaShowClause(clause)) {
      throw new Error(`attempted to save a behavior inside a formula.`);
    }

    const model = clause.behavior?.id ? behaviors.getOne(clause.behavior.id) : null;

    // checks for valid behavior permissions
    if (!omitPermissionsCheck && model?.value && !model?.value?.can_update_basic) {
      return t(`query-builder.behaviors.behavior-cannot-edit`);
    }

    return null;
  };

  const getSaveMetricDisabledMessage = ({
    index,
    parentMetricIndex,
    omitPermissionsCheck = false,
  }: {
    index: number;
    parentMetricIndex?: number;
    omitPermissionsCheck?: boolean;
  }): string | null => {
    const clause = getMetricAtIndex({
      index,
      parentMetricIndex,
      params: bookmarkParams,
    });

    if (!clause) {
      return null;
    }

    const model = clause.id ? metrics.getOne(clause.id) : null;

    // checks for valid metric permissions
    if (!omitPermissionsCheck && model?.value && !model?.value?.can_update_basic) {
      return t(`query-builder.metrics.metric-cannot-edit`);
    }

    if (isFormulaShowClause(clause)) {
      const {isValid, referencedMetrics} = getUpdatedFormulaAndMetrics({
        formula: clause,
        // when saving a formula, the metrics referenced in it can come from the main report or metrics inside the formula
        clauses: clause.referencedMetrics?.length ? clause.referencedMetrics : bookmarkParams.sections.show,
      });

      const usesMultiAttributionMetric = Boolean(
        referencedMetrics.filter((c) => c.measurement?.multiAttribution).length,
      );
      const usesCountUsersOnceMetric = Boolean(referencedMetrics.filter((c) => c.measurement?.segmentMethod).length);

      if (!isValid) {
        return t(`query-builder.metrics.formula-cannot-save-nested`);
      } else if (usesMultiAttributionMetric) {
        return t(`query-builder.metrics.formula-cannot-save-multi-attribution`);
      } else if (usesCountUsersOnceMetric) {
        return t(`query-builder.metrics.formula-cannot-save-count-users-once`);
      }
      return null;
    }

    return null;
  };

  const getClause = ({index, parentMetricIndex}: {index: number; parentMetricIndex?: number}) => {
    return getMetricAtIndex({
      index,
      parentMetricIndex,
      params: bookmarkParams,
    });
  };

  return {
    getClause,
    resetMetric,
    saveMetric,
    selectMetricItem,
    resetBehavior,
    saveBehavior,
    getSaveMetricDisabledMessage,
    getSaveBehaviorDisabledMessage,
  };
}
